import { useEffect, useMemo, useRef, useState } from 'preact/hooks';
import { AppLayout } from '../../components/app/AppLayout';
import { RequireOnboarded } from '../../components/app/RequireOnboarded';
import { useI18n, LOCALES, type Locale } from '../../i18n';
import { useAuth } from '../../lib/auth';
import { useApiAuth } from '../../lib/useApi';
import { useProfile } from '../../state/profile';
import { useGameData } from '../../state/game';
import { BottleCap } from '../../components/app/BottleCap';
import { CantonShield } from '../../components/app/CantonShield';
import { QrCode } from '../../components/app/QrCode';
import { Icon } from '../../components/app/icons';
import { getMyRank, type MeRank } from '../../lib/content';
import { listVouchers, submitVoucherAnswers, type Voucher } from '../../lib/vouchers';
import { deleteAvatar, saveProfile, setAutoPick, uploadAvatar } from '../../lib/profile';
import { loadCantons, localizedName, type Canton } from '../../lib/static';
import { deleteAccount } from '../../lib/api';
import { outcomeOf } from '../../components/app/CrowdPicksBar';
import { PredictionRow, PredictionGroup, type PredRow } from '../../components/app/PredictionList';
import { sideLabel, gradeScore } from '../../lib/wcTypes';
import { shortDate, shortTime } from '../../lib/datetime';

export function Profile() {
  return (
    <RequireOnboarded>
      <AppLayout>
        <ProfileInner />
      </AppLayout>
    </RequireOnboarded>
  );
}

function ProfileInner() {
  const { t, locale, setLocale } = useI18n();
  const { user, accessToken, signOut } = useAuth();
  const auth = useApiAuth();
  const { profile, mergePatch } = useProfile();
  const { bundle, rules, predictions, pointsFor, boostsUsed } = useGameData();

  const [rank, setRank] = useState<MeRank | null>(null);
  const [vouchers, setVouchers] = useState<Voucher[]>([]);
  const [cantons, setCantons] = useState<Canton[]>([]);
  const [cantonOpen, setCantonOpen] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    getMyRank(auth).then(setRank).catch(() => {});
    listVouchers(auth).then(setVouchers).catch(() => {});
    loadCantons().then(setCantons).catch(() => {});
  }, []);

  // Accuracy + best streak across finished predictions (correct 1X2 outcome),
  // walked in kickoff order so the streak reflects real chronology.
  const { played, correct, bestStreak } = useMemo(() => {
    let played = 0;
    let correct = 0;
    let bestStreak = 0;
    let run = 0;
    if (bundle) {
      const done = bundle.matches
        .filter((m) => m.status === 'done' && m.score_a != null && m.score_b != null && predictions.has(m.number))
        .sort((a, b) => a.kickoff_utc.localeCompare(b.kickoff_utc));
      for (const m of done) {
        const p = predictions.get(m.number)!;
        const g = gradeScore(m)!; // 90' full-time — what points are graded on
        played++;
        if (outcomeOf(p.scoreA, p.scoreB) === outcomeOf(g[0], g[1])) {
          correct++;
          run++;
          if (run > bestStreak) bestStreak = run;
        } else {
          run = 0;
        }
      }
    }
    return { played, correct, bestStreak };
  }, [bundle, predictions]);
  const accuracy = played ? Math.round((correct / played) * 100) : 0;

  // All of the user's picks, split into Active (upcoming + live) and
  // Previous (done), each collapsible — the full history, not just recent.
  const predRows = useMemo(() => {
    if (!bundle) return { active: [] as PredRow[], previous: [] as PredRow[] };
    const rows: PredRow[] = bundle.matches
      .filter((m) => predictions.has(m.number))
      .map((m) => {
        const p = predictions.get(m.number)!;
        const done = m.status === 'done';
        return {
          matchNumber: m.number,
          capA: m.team_a,
          flagA: m.team_a ? bundle.teams[m.team_a]?.flag : undefined,
          labelA: sideLabel(bundle, m.team_a, m.team_a_slot, locale),
          capB: m.team_b,
          flagB: m.team_b ? bundle.teams[m.team_b]?.flag : undefined,
          labelB: sideLabel(bundle, m.team_b, m.team_b_slot, locale),
          status: m.status,
          predictedA: p.scoreA,
          predictedB: p.scoreB,
          actualA: done ? gradeScore(m)?.[0] : undefined,
          actualB: done ? gradeScore(m)?.[1] : undefined,
          points: done ? pointsFor(m.number) : undefined,
          boost: p.boost,
          dateLabel: `${shortDate(m.kickoff_utc, locale)} · ${shortTime(m.kickoff_utc, locale)}`,
        };
      });
    return {
      active: rows.filter((r) => r.status !== 'done').sort((a, b) => a.matchNumber - b.matchNumber),
      previous: rows.filter((r) => r.status === 'done').sort((a, b) => b.matchNumber - a.matchNumber),
    };
  }, [bundle, predictions, locale, pointsFor]);

  async function onCanton(code: string) {
    if (!profile) return;
    try {
      await saveProfile(auth, { nickname: profile.nickname, avatarCap: profile.avatarCap, canton: code });
      mergePatch({ canton: code });
    } catch {}
  }
  async function onAutoPick(v: boolean) {
    try {
      await setAutoPick(auth, v);
      mergePatch({ autoPick: v });
    } catch {}
  }
  async function onAvatar(e: Event) {
    const f = (e.target as HTMLInputElement).files?.[0];
    if (!f) return;
    try {
      const url = await uploadAvatar(auth, f);
      mergePatch({ avatarUrl: url });
    } catch {
      alert(t('profile.avatar_failed'));
    }
  }
  async function onAvatarRemove() {
    try {
      await deleteAvatar(auth);
      mergePatch({ avatarUrl: '' });
    } catch {}
  }
  async function onLogout() {
    await signOut();
    location.replace('/' + locale);
  }
  async function onDelete() {
    if (!accessToken || !confirm(t('profile.delete_confirm'))) return;
    try {
      await deleteAccount(accessToken);
      await signOut();
      location.replace('/' + locale);
    } catch {
      alert(t('profile.error'));
    }
  }

  const boostsLeft = rules.boostTokens - boostsUsed;

  return (
    <div class="pf">
      <div class="pf__head">
        <div class="pf__avatar">
          {profile?.avatarUrl ? (
            <img src={profile.avatarUrl} alt="" />
          ) : (
            <BottleCap code={profile?.avatarCap} size={72} />
          )}
        </div>
        <div class="pf__id">
          <h1 class="bc-serif">{profile?.nickname ?? user?.displayName}</h1>
          <p>
            <CantonShield code={profile?.canton} size={16} /> {profile?.canton ?? ''}
          </p>
          <div class="pf__avatar-actions">
            <button class="bc-pill" onClick={() => fileRef.current?.click()}>{t('profile.change_avatar')}</button>
            {profile?.avatarUrl && (
              <button class="bc-pill" onClick={onAvatarRemove}>{t('profile.remove_avatar')}</button>
            )}
            <input ref={fileRef} type="file" accept="image/*" hidden onChange={onAvatar} />
          </div>
        </div>
      </div>

      <div class="pf__stats">
        <Stat label={t('profile.rank')} value={rank?.rank != null ? `#${rank.rank}` : '–'} />
        <Stat label={t('profile.points')} value={rank?.pts ?? 0} />
        <Stat label={t('profile.picks')} value={predictions.size} />
        <Stat label={t('profile.accuracy')} value={`${accuracy}%`} />
        <Stat label={t('profile.correct')} value={correct} />
        <Stat label={t('profile.streak')} value={bestStreak} />
      </div>

      <div class="bc-card pf__boosts">
        <span class="bc-label">{t('profile.boosts')}</span>
        <div class="pf__boostdots">
          {Array.from({ length: rules.boostTokens }, (_, i) => (
            <span key={i} class={`pf__bolt${i < boostsLeft ? ' is-on' : ''}`}>
              <Icon.boost size={20} />
            </span>
          ))}
          <span class="pf__boostcount">{boostsLeft}/{rules.boostTokens}</span>
        </div>
      </div>

      {/* All predictions — collapsible Active / Previous groups */}
      <section class="pf__preds">
        <span class="bc-label">{t('pubprofile.predictions')}</span>
        {predRows.active.length === 0 && predRows.previous.length === 0 ? (
          <p class="bc-empty">{t('pubprofile.no_predictions')}</p>
        ) : (
          <>
            {predRows.active.length > 0 && (
              <PredictionGroup label={t('preds.active')} count={predRows.active.length}>
                {predRows.active.map((r) => (
                  <PredictionRow key={r.matchNumber} row={r} />
                ))}
              </PredictionGroup>
            )}
            {predRows.previous.length > 0 && (
              <PredictionGroup
                label={t('preds.previous')}
                count={predRows.previous.length}
                defaultOpen={false}
                accent="mute"
              >
                {predRows.previous.map((r) => (
                  <PredictionRow key={r.matchNumber} row={r} />
                ))}
              </PredictionGroup>
            )}
          </>
        )}
      </section>

      {/* Vouchers */}
      <section>
        <span class="bc-label">{t('profile.vouchers')}</span>
        {vouchers.length === 0 ? (
          <p class="bc-empty">{t('profile.no_vouchers')}</p>
        ) : (
          <div class="pf__vouchers">
            {vouchers.map((v) => (
              <VoucherCard key={v.id} v={v} auth={auth} onSaved={() => listVouchers(auth).then(setVouchers)} />
            ))}
          </div>
        )}
      </section>

      {/* Settings */}
      <section class="bc-card pf__settings">
        <span class="bc-label">{t('profile.settings')}</span>

        <div class="pf__setting">
          <span>{t('profile.canton')}</span>
          <button class="pf__cantonbtn" onClick={() => setCantonOpen(true)}>
            <CantonShield code={profile?.canton} size={20} />
            <span>
              {profile?.canton
                ? localizedName(cantons.find((c) => c.code === profile.canton)?.name, locale) || profile.canton
                : '—'}
            </span>
            <Icon.chevron size={14} />
          </button>
        </div>

        <div class="pf__setting">
          <span>{t('profile.autopick')}</span>
          <button
            type="button"
            role="switch"
            aria-checked={!!profile?.autoPick}
            class={`bc-switch${profile?.autoPick ? ' is-on' : ''}`}
            onClick={() => onAutoPick(!profile?.autoPick)}
          >
            <span class="bc-switch__knob" />
          </button>
        </div>

        <div class="pf__setting">
          <span>{t('profile.language')}</span>
          <div class="bc-lang pf__lang">
            {LOCALES.map((l: Locale) => (
              <button key={l} class={l === locale ? 'is-active' : ''} onClick={() => setLocale(l)}>{l.toUpperCase()}</button>
            ))}
          </div>
        </div>

        <div class="pf__danger">
          <button class="bc-pill" onClick={onLogout}>
            <Icon.logout size={15} /> {t('header.menu.logout')}
          </button>
          <button class="bc-pill pf__del" onClick={onDelete}>{t('profile.delete')}</button>
        </div>
      </section>

      {cantonOpen && (
        <div class="bc-sheet__scrim" onClick={() => setCantonOpen(false)}>
          <div class="bc-sheet bc-sheet--tall" onClick={(e) => e.stopPropagation()}>
            <h2 class="bc-sheet__title">{t('profile.canton')}</h2>
            <div class="pf__cantongrid">
              {cantons.map((c) => (
                <button
                  key={c.code}
                  class={`pf__cantoncell${profile?.canton === c.code ? ' is-active' : ''}`}
                  onClick={() => { onCanton(c.code); setCantonOpen(false); }}
                >
                  <CantonShield code={c.code} size={26} />
                  <span>{localizedName(c.name, locale)}</span>
                </button>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Stat({ label, value }: { label: string; value: string | number }) {
  return (
    <div class="pf__stat">
      <span class="pf__statval">{value}</span>
      <span class="pf__statlbl">{label}</span>
    </div>
  );
}

function VoucherCard({ v, auth, onSaved }: { v: Voucher; auth: ReturnType<typeof useApiAuth>; onSaved: () => void }) {
  const { t } = useI18n();
  const [open, setOpen] = useState(false);
  const [answers, setAnswers] = useState<Record<string, string>>(v.answers ?? {});
  const [busy, setBusy] = useState(false);
  const needsAnswers = (v.questions?.length ?? 0) > 0;

  async function save() {
    setBusy(true);
    try {
      await submitVoucherAnswers(auth, v.id, answers);
      setOpen(false);
      onSaved();
    } catch {
      /* ignore */
    } finally {
      setBusy(false);
    }
  }

  return (
    <div class="pf__voucher">
      <div class="pf__voucher-top">
        <strong>{v.kind === 'drink' ? (v.label ?? t('profile.drink')) : v.articleTitle}</strong>
        <span class={`pf__vstate pf__vstate--${v.state}`}>{t(`profile.vstate.${v.state}`) || v.state}</span>
      </div>
      {v.partnerName && <span class="pf__vpartner">{v.partnerName}</span>}
      {v.qrToken && (
        <div class="pf__qrwrap">
          <QrCode value={v.qrToken} size={172} />
          <span class="pf__qrhint">{t('profile.redeem_hint')}</span>
        </div>
      )}
      {needsAnswers && (
        <>
          <button class="bc-pill" onClick={() => setOpen((o) => !o)}>{t('profile.answer')}</button>
          {open && (
            <div class="pf__qa">
              {v.questions!.map((q) => (
                <label key={q.key} class="onb-field">
                  <span>{q.label}</span>
                  <input
                    class="bc-input"
                    value={answers[q.key] ?? ''}
                    onInput={(e) => setAnswers({ ...answers, [q.key]: (e.target as HTMLInputElement).value })}
                  />
                </label>
              ))}
              <button class="bc-cta" disabled={busy} onClick={save}>{busy ? t('onb.saving') : t('profile.submit')}</button>
            </div>
          )}
        </>
      )}
    </div>
  );
}
